import { styled } from '@mui/material/styles';
import { Box, Paper } from '@mui/material';
import { FONT_SIZES, FONT_WEIGHTS } from '../../../Theme';

export const getQuietFieldSx = (theme) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 'var(--radius-ui)',
    backgroundColor: theme.palette.background.paper,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.border.subtle,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.primary,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.primary,
      borderWidth: '1px',
    },
  },
});

export const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  padding: 0,
  border: 'none',
  boxShadow: 'none',
  backgroundColor: 'transparent',
}));

export const SectionTitle = styled(Box)(({ theme }) => ({
  fontSize: FONT_SIZES.caption,
  fontWeight: FONT_WEIGHTS.medium,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

export const FieldWrapper = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '&:last-child': {
    marginBottom: 0,
  },
}));

export const FieldLabel = styled(Box)(({ theme }) => ({
  fontSize: FONT_SIZES.caption,
  fontWeight: FONT_WEIGHTS.medium,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.75),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

export const SegmentTrack = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  padding: 3,
  gap: 2,
  borderRadius: 'var(--radius-ui)',
  backgroundColor: theme.palette.background.soft,
}));

export const SegmentOption = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  flex: '1 1 0',
  minWidth: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 36,
  padding: theme.spacing(0.75, 1),
  border: 0,
  margin: 0,
  appearance: 'none',
  fontFamily: theme.typography.fontFamily,
  fontSize: FONT_SIZES.caption,
  fontWeight: selected ? FONT_WEIGHTS.medium : FONT_WEIGHTS.regular,
  lineHeight: 1.25,
  textAlign: 'center',
  cursor: 'pointer',
  borderRadius: 'calc(var(--radius-ui) - 2px)',
  color: selected ? theme.palette.text.primary : theme.palette.text.secondary,
  backgroundColor: selected ? theme.palette.background.paper : 'transparent',
  '&:hover': {
    color: theme.palette.text.primary,
  },
}));

export const ChoiceList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    '& > *': {
      flex: '1 1 0',
      minWidth: 0,
    },
  },
}));

export const ChoiceRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: theme.spacing(1.25),
  width: '100%',
  minHeight: 72,
  padding: theme.spacing(1.25),
  borderRadius: 'var(--radius-ui)',
  border: `1px solid ${theme.palette.background.softHover}`,
  backgroundColor: selected ? theme.palette.background.soft : 'transparent',
  '&:hover': {
    backgroundColor: selected ? theme.palette.background.softHover : theme.palette.background.soft,
  },
}));

export const ChoiceRowButton = styled('button')(({ theme }) => ({
  display: 'block',
  width: '100%',
  padding: 0,
  border: 0,
  margin: 0,
  appearance: 'none',
  backgroundColor: 'transparent',
  textAlign: 'left',
  cursor: 'pointer',
  fontFamily: theme.typography.fontFamily,
  fontSize: FONT_SIZES.bodySm,
  fontWeight: FONT_WEIGHTS.medium,
  lineHeight: 1.35,
  color: theme.palette.text.primary,
  whiteSpace: 'normal',
}));

export const ChipContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.75),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.65),
  },
}));

export const CategoryPill = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: theme.spacing(0.5, 1.25),
  borderRadius: 'var(--radius-pill)',
  border: `1px solid ${selected ? theme.palette.background.soft : theme.palette.background.softHover}`,
  fontSize: FONT_SIZES.caption,
  fontWeight: selected ? FONT_WEIGHTS.medium : FONT_WEIGHTS.regular,
  fontFamily: theme.typography.fontFamily,
  lineHeight: 1.3,
  cursor: 'pointer',
  appearance: 'none',
  color: selected ? theme.palette.text.primary : theme.palette.text.secondary,
  backgroundColor: selected ? theme.palette.background.soft : 'transparent',
  '&:hover:not(:disabled)': {
    color: theme.palette.text.primary,
    backgroundColor: selected ? theme.palette.background.softHover : theme.palette.background.soft,
    borderColor: selected ? theme.palette.background.softHover : theme.palette.background.soft,
  },
  '&:disabled': {
    color: theme.palette.text.disabled,
    cursor: 'not-allowed',
    backgroundColor: 'transparent',
    borderColor: theme.palette.background.soft,
  },
}));

export const SelectableChip = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: theme.spacing(0.875, 1.75),
  borderRadius: 'var(--radius-ui)',
  fontSize: FONT_SIZES.bodySm,
  fontWeight: FONT_WEIGHTS.medium,
  fontFamily: theme.typography.fontFamily,
  lineHeight: 1.2,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  color: selected ? theme.palette.favorite.main : theme.palette.text.secondary,
  backgroundColor: selected ? theme.palette.grey[50] : theme.palette.background.paper,
  border: `1px solid ${selected ? theme.palette.favorite.main : theme.palette.border.subtle}`,
  [theme.breakpoints.down('sm')]: {
    fontSize: FONT_SIZES.bodySm,
    padding: theme.spacing(0.9, 1.75),
  },
  '&:hover': {
    borderColor: theme.palette.favorite.main,
    color: selected ? theme.palette.favorite.main : theme.palette.text.primary,
    backgroundColor: theme.palette.grey[50],
  },
}));

export const CreateCampaignTopBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
}));

export const CreateCampaignTopBarSide = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  alignSelf: 'center',
  minWidth: 0,
  flexShrink: 0,
});

export const CreateCampaignTopBarActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
  minWidth: 0,
  flexShrink: 0,
  flexWrap: 'wrap',
  '& .MuiButton-root': {
    alignSelf: 'center',
    maxWidth: '100%',
  },
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.75),
  },
}));

export const CreateCampaignHeaderStack = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  minWidth: 0,
  flex: 1,
});

export const PageHeader = styled(Box)({
  textAlign: 'left',
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  '& h1': {
    marginBottom: 0,
  },
});

// Collapsible section header
export const CollapsibleHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  padding: theme.spacing(1, 0),
  marginBottom: theme.spacing(2),
  border: 'none',
  backgroundColor: 'transparent',
  '&:hover': {
    color: theme.palette.text.primary,
  },
}));

// ── Preview Panel ────────────────────────────────────────────────

export const PreviewCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'flat',
})(({ theme, flat }) => ({
  padding: flat ? 0 : theme.spacing(2.5),
  borderRadius: flat ? 0 : 'var(--radius-ui)',
  border: flat ? 'none' : `1px solid ${theme.palette.border.subtle}`,
  backgroundColor: flat ? 'transparent' : theme.palette.background.paper,
  boxShadow: 'none',
  overflow: 'hidden',
  minWidth: 0,
  '& .preview-title': {
    fontSize: FONT_SIZES.caption,
    fontWeight: FONT_WEIGHTS.strong,
    color: theme.palette.text.secondary,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(1.5),
  },
}));
